// Pure helpers of the public homepage (no DOM, no globals): visitor mood messages, the
// toggle statistics line, zone button state, countdown text, camera labels and laptop form
// texts. Used by the browser bundle (client/main.ts) and by the server-side renderer
// (http/home.ts), so the first paint and later live updates read the same. Unit-tested
// in Node (site/test/unit/homeClient.test.ts).
import { apiMessage, formatDuration } from '../admin/lib.js';

export type OnOff = 'ON' | 'OFF';

// --- visitors and statistics (Req 18, 19, design "Public site layout") ----------------

/**
 * Mood bands carried over from the current site as decoration on the real count,
 * checked from the top. 69 is special-cased in moodMessage. Thresholds match the legacy
 * site (5 / 15 / 25 / 50 / 70 / 100 / 250 / 500); 0 shows "Slow Time".
 */
export const MOOD_BANDS: readonly { readonly min: number; readonly text: string }[] = Object.freeze([
  { min: 500, text: 'SERVER OVERLOAD' },
  { min: 250, text: 'Servers feeling it' },
  { min: 100, text: 'Bring it on !' },
  { min: 70, text: 'Crazy !' },
  { min: 50, text: 'Fairly Hectic' },
  { min: 25, text: 'Where was this featured ?' },
  { min: 15, text: 'Popular' },
  { min: 5, text: 'Not Bad' },
  { min: 0, text: 'Slow Time' },
]);

export function moodMessage(count: number): string {
  const n = Number.isFinite(count) ? Math.max(0, Math.floor(count)) : 0;
  if (n === 69) return 'NICE!';
  return MOOD_BANDS.find((b) => n >= b.min)?.text ?? 'Slow Time';
}

export function visitorsText(count: number): string {
  return String(count) + (count === 1 ? ' person watching' : ' people watching');
}

/** "Toggled X times today including N overloads" (Req 19.1); null when unavailable (19.7). */
export function statsText(togglesToday: number | null, overloadsToday: number | null): string | null {
  if (togglesToday === null || overloadsToday === null) return null;
  return (
    'Toggled ' +
    String(togglesToday) +
    (togglesToday === 1 ? ' time' : ' times') +
    ' today including ' +
    String(overloadsToday) +
    (overloadsToday === 1 ? ' overload' : ' overloads')
  );
}

export const STATS_UNAVAILABLE = 'Toggle statistics are currently unavailable.';

// --- zones (Req 3.1, 3.6, 3.8, 1.4, 24.4) ----------------------------------------------

export interface ZoneLike {
  readonly n: number;
  readonly name: string;
  readonly description: string;
  readonly requested: OnOff;
  readonly reported: string;
  readonly pending: boolean;
}

export interface ControlContext {
  /** null = unknown (section unavailable): treated as not live. */
  readonly controlsLive: boolean | null;
  /** HA_Link connected; null = unknown (not treated as down). */
  readonly reachable: boolean | null;
  readonly showRunning: boolean;
}

export interface ZoneView {
  /** Text label next to the colour (Req 1.4): Reported_State, or "unknown". */
  readonly label: 'ON' | 'OFF' | 'unknown';
  readonly pending: boolean;
  /** Every control of the zone disabled. */
  readonly disabled: boolean;
  /** Desktop ON button: only sends while the zone is OFF (and the other way round). */
  readonly onEnabled: boolean;
  readonly offEnabled: boolean;
  /** Mobile single toggle: what pressing it does. */
  readonly toggleText: string;
}

export function reportedLabel(reported: string): 'ON' | 'OFF' | 'unknown' {
  return reported === 'ON' || reported === 'OFF' ? reported : 'unknown';
}

export function controlsDisabled(ctx: ControlContext): boolean {
  return ctx.controlsLive !== true || ctx.reachable === false || ctx.showRunning;
}

/** Why the zone controls are disabled, or null while they are usable. */
export function disabledReason(ctx: ControlContext): string | null {
  if (ctx.controlsLive !== true) return 'The controls are offline right now. Have a look at the countdown.';
  if (ctx.reachable === false) return 'The lights may not respond: the house is not connected right now.';
  if (ctx.showRunning) return 'A light show is running. The controls are back when it has finished.';
  return null;
}

export function zoneView(z: ZoneLike, ctx: ControlContext): ZoneView {
  const disabled = controlsDisabled(ctx);
  return {
    label: reportedLabel(z.reported),
    pending: z.pending,
    disabled,
    // The buttons follow the Requested_State: a toggle flips it, so ON only makes sense
    // while it is OFF.
    onEnabled: !disabled && z.requested === 'OFF',
    offEnabled: !disabled && z.requested === 'ON',
    toggleText: z.requested === 'ON' ? 'Switch off' : 'Switch on',
  };
}

/** Whether pressing `button` should send a toggle for a zone requested `requested`. */
export function shouldSendToggle(button: 'ON' | 'OFF' | 'toggle', requested: OnOff): boolean {
  if (button === 'toggle') return true;
  return button === 'ON' ? requested === 'OFF' : requested === 'ON';
}

/**
 * Message for a refused toggle (cooldown seconds, house busy, rate limited, offline,
 * unreachable, show running). status 0 = the request never reached the site.
 */
export function toggleMessage(status: number, body: unknown): string {
  if (status === 0) return 'Could not reach the site. Please try again.';
  const b = (typeof body === 'object' && body !== null ? body : {}) as { error?: unknown };
  if (b.error === 'csrf_failed' || b.error === 'forbidden_origin')
    return 'Your session has expired. Please reload the page and try again.';
  return apiMessage(status, body);
}

// --- countdown (Req 10) ----------------------------------------------------------------

export interface CountdownLike {
  readonly state: 'ONLINE' | 'OFFLINE';
  readonly kind: string;
  readonly label: string;
  readonly endsAt?: number;
  readonly startsAt?: number;
}

export interface CountdownView {
  readonly status: 'ONLINE' | 'OFFLINE';
  readonly label: string;
  /** Date and time of the next start (OFFLINE with a countdown), else null. */
  readonly when: string | null;
  /** Remaining time, never negative; null when there is no countdown. */
  readonly time: string | null;
  /** Instant the countdown runs to, or null. */
  readonly target: number | null;
  /** Text announced to assistive technologies: changes only with status / label. */
  readonly announce: string;
}

const WHEN = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/London',
  weekday: 'short',
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

/** "Tue 1 Dec 2026, 16:00" in Site_Timezone. */
export function formatWhen(t: number): string {
  return WHEN.format(new Date(t)).replace(/^(\w{3}),/, '$1');
}

const DAY_MS = 86_400_000;

/** Countdown text for server time `now` (device time + clock offset). */
export function countdownView(c: CountdownLike, now: number): CountdownView {
  const make = (label: string, target: number | null, when: string | null, days: boolean): CountdownView => {
    const left = target === null ? null : Math.max(0, target - now);
    return {
      status: c.state,
      label,
      when,
      time: left === null ? null : formatDuration(left, days && left >= DAY_MS),
      target,
      announce: c.state + ' – ' + label + (when ? ' ' + when : ''),
    };
  };
  switch (c.kind) {
    case 'live':
      // HH:MM:SS, hours may exceed 24 (Req 10.1: "25:00:00 left" on Christmas Eve).
      return make('Controls live', c.endsAt ?? null, null, false);
    case 'next_show':
      return make('Next show', c.startsAt ?? null, c.startsAt ? formatWhen(c.startsAt) : null, true);
    case 'next_season':
      return make('Next season starts', c.startsAt ?? null, c.startsAt ? formatWhen(c.startsAt) : null, true);
    case 'manual_override':
      return make('Manual override', null, null, false);
    case 'controls_disabled':
      return make('Controls disabled', null, null, false);
    case 'no_shows':
      return make('No shows scheduled', null, null, false);
    default:
      return make(c.label, null, null, false);
  }
}

/** The countdown reached zero: the page should fetch the new state (Req 10.6). */
export function countdownExpired(c: CountdownLike, now: number): boolean {
  const target = c.state === 'ONLINE' ? c.endsAt : c.startsAt;
  return target !== undefined && now >= target;
}

// --- cameras (Req 11.12, 12) -----------------------------------------------------------

export interface CameraLike {
  readonly n: number;
  readonly name: string;
  readonly status: 'LIVE' | 'OFFLINE';
  readonly reason?: string;
}

export function cameraLabel(c: CameraLike): { readonly label: 'Live' | 'Offline'; readonly reason: string } {
  return c.status === 'LIVE' ? { label: 'Live', reason: '' } : { label: 'Offline', reason: c.reason ?? '' };
}

/** Snapshot URL; `n` makes every request unique so no cache can answer it. */
export function cameraUrl(cam: number, n: number): string {
  return '/cam/' + String(cam) + '.jpg?t=' + String(n);
}

// --- laptop form (Req 13) --------------------------------------------------------------

export const LAPTOP_MAX = 140;

/** Characters typed, counted like the server (code points of the trimmed text). */
export function laptopLength(text: string): number {
  return [...text.trim()].length;
}

export function laptopCounterText(text: string): string {
  const left = LAPTOP_MAX - laptopLength(text);
  return left >= 0
    ? String(left) + ' of ' + String(LAPTOP_MAX) + ' characters left'
    : String(-left) + ' characters too many';
}

export interface LaptopOutcome {
  readonly ok: boolean;
  /** Clear the form (only after the message was stored, Req 13.11). */
  readonly clear: boolean;
  readonly message: string;
}

export function laptopOutcome(status: number, body: unknown): LaptopOutcome {
  if (status === 0)
    return { ok: false, clear: false, message: 'Could not reach the site. Please try again.' };
  const b = (typeof body === 'object' && body !== null ? body : {}) as { status?: unknown; error?: unknown };
  if (status === 201) {
    return {
      ok: true,
      clear: true,
      message:
        b.status === 'pending'
          ? 'Thanks! Your message is awaiting review.'
          : 'Thanks! Your message was sent.',
    };
  }
  if (b.error === 'csrf_failed' || b.error === 'forbidden_origin')
    return {
      ok: false,
      clear: false,
      message: 'Your session has expired. Please reload the page and try again.',
    };
  return { ok: false, clear: false, message: apiMessage(status, body) };
}

// --- JSON in HTML ----------------------------------------------------------------------

/**
 * JSON for an inline <script type="application/json"> block: `<`, `>`, `&` and the line
 * separators are escaped so the text can never close the element or start markup.
 */
export function jsonForHtml(value: unknown): string {
  return JSON.stringify(value)
    .replace(/</g, '\\u003c')
    .replace(/>/g, '\\u003e')
    .replace(/&/g, '\\u0026')
    .replace(/\u2028/g, '\\u2028')
    .replace(/\u2029/g, '\\u2029');
}
